import { gql } from 'graphql-tag'

/**
 * Mutation to create a case-stage relation (assign stage to case)
 */
export const CREATE_CASE_STAGE_RELATION_MUTATION = gql`
  mutation CreateCaseStageRelation($caseId: ID!, $stageId: ID!) {
    createCaseStageRelation(
      data: {
        case: { connect: { id: $caseId } }
        stage: { connect: { id: $stageId } }
      }
    ) {
      id
      case {
        id
      }
      stage {
        id
        name
      }
      assignedAt
    }
  }
`

/**
 * Temporary shortcut to flip a case into the configured "Lost" stage.
 * Server-side picks the lowest-displayOrder CaseStage whose category
 * is `lost`. Restricted to SysAdmin / LeadAgencyOwner.
 */
export const MARK_CASE_AS_LOST_MUTATION = gql`
  mutation MarkCaseAsLost($caseId: ID!) {
    markCaseAsLost(caseId: $caseId)
  }
`

/**
 * Shortcut to flip a case into the configured "Won" stage. Server-side
 * picks the lowest-displayOrder CaseStage whose category is `won`.
 * Restricted to SysAdmin / LeadAgencyOwner.
 */
export const MARK_CASE_AS_WON_MUTATION = gql`
  mutation MarkCaseAsWon($caseId: ID!) {
    markCaseAsWon(caseId: $caseId)
  }
`

/**
 * Reverse of mark-as-lost: restore a lost case to the active stage it
 * held before. Server picks the stage and re-activates. Restricted to
 * SysAdmin / LeadAgencyOwner.
 */
export const REACTIVATE_CASE_MUTATION = gql`
  mutation ReactivateCase($caseId: ID!) {
    reactivateCase(caseId: $caseId)
  }
`

/**
 * Mutation to set/move the current user's plan (next planned day) for a case.
 * `plannedFor` is a "YYYY-MM-DD" day. `userId` (optional, managers only)
 * plans for another user.
 */
export const SET_CASE_PLAN_MUTATION = gql`
  mutation SetCasePlan($caseId: ID!, $plannedFor: String!, $userId: ID) {
    setCasePlan(caseId: $caseId, plannedFor: $plannedFor, userId: $userId)
  }
`

/**
 * Reassign a case to another lead-agency member ("attended by"). The previous
 * assignee's plan is removed and the new one gets it planned for TODAY.
 * Returns { id, assignedTo: { id, name, shortName, label, color, role } }.
 * (There is no removeCasePlan — un-planning doesn't exist; plans only move.)
 */
export const ASSIGN_CASE_TO_USER_MUTATION = gql`
  mutation AssignCaseToUser($caseId: ID!, $userId: ID!) {
    assignCaseToUser(caseId: $caseId, userId: $userId)
  }
`

/**
 * Mutation to update only generalNotes for a case
 */
/**
 * Mutation to analyze free-form text with AI and extract structured case fields.
 * Returns a JSON blob — no case is created in the database.
 */
export const ANALYZE_TEXT_FOR_CASE_MUTATION = gql`
  mutation AnalyzeTextForCase($text: String!) {
    analyzeTextForCase(text: $text)
  }
`

// PERMANENT delete (Sandy 2026-10-30): erased from the database — only a case of
// the default NEW CASES company, only SysAdmin / LeadAgencyOwner (server-checked).
export const DELETE_CASE_PERMANENTLY_MUTATION = gql`
  mutation DeleteCasePermanently($caseId: ID!) {
    deleteCasePermanently(caseId: $caseId)
  }
`

export const SOFT_REMOVE_CASE_MUTATION = gql`
  mutation SoftRemoveCase($caseId: ID!) {
    softRemoveCase(caseId: $caseId)
  }
`

export const UPDATE_CASE_GENERAL_NOTES_MUTATION = gql`
  mutation UpdateCaseGeneralNotes($caseId: ID!, $generalNotes: String) {
    updateCase(where: { id: $caseId }, data: { generalNotes: $generalNotes }) {
      id
      generalNotes
      updatedAt
    }
  }
`

/**
 * Mutation to add a tag to a case.
 */
export const ADD_TAG_TO_CASE_MUTATION = gql`
  mutation AddTagToCase($caseId: ID!, $tagId: ID!) {
    addTagToCase(caseId: $caseId, tagId: $tagId)
  }
`

/**
 * Mutation to remove a tag from a case.
 */
export const REMOVE_TAG_FROM_CASE_MUTATION = gql`
  mutation RemoveTagFromCase($caseId: ID!, $tagId: ID!) {
    removeTagFromCase(caseId: $caseId, tagId: $tagId)
  }
`

/**
 * Set diagnostics and insurance info for a case (used after create/import).
 */
export const SET_DIAGNOSTICS_AND_INSURANCE_INFO_MUTATION = gql`
  mutation SetDiagnosticsAndInsuranceInfo(
    $caseId: ID!
    $diagnostics: JSON
    $insurances: JSON
  ) {
    setDiagnosticsAndInsuranceInfo(
      caseId: $caseId
      diagnostics: $diagnostics
      insurances: $insurances
    )
  }
`

/**
 * Mutation to create a case.
 */
export const CREATE_CASE_MUTATION = gql`
  mutation CreateCase($data: CaseCreateInput!) {
    createCase(data: $data) {
      id
      title
      generalNotes
      motherName
      fatherName
      otherContactName
      contactPhone
      contactEmail
      contactLanguage
      homeAddress
      homeCity
      homeState
      homeZipCode
      schoolName
      schoolAddress
      schoolCity
      schoolState
      schoolZipCode
      therapyStartTime
      therapyEndTime
      availabilityHours
      availabilityNotes
      childName
      childDateOfBirth
      childAge
      childGender
      childLanguage
      caseStatus
      caseProgress
      caseType
      otherTherapy
      caseInsurancesNotes
      initialDocuments
      bcba { id firstName lastName nickName category }
      bcaba { id firstName lastName nickName category }
      rbt { id firstName lastName nickName category }
      bcbaNotes
      rbtNotes
      leadSource {
        id
        name
        shortName
        county
      }
      company {
        id
        name
        shortName
        isDefault
      }
      tags {
        tag {
          id
          label
          category
          displayOrder
          icon
          color
        }
      }
      currentStage
      therapyPlace
      unreadTrackingCount
      checklist
      startedAt
      createdAt
      updatedAt
      myPlan
    }
  }
`

/**
 * Mutation to import a case. The backend assigns it to the importer, creates
 * the initial ACTIVE stage (planned for TODAY via the assignment invariant),
 * and adds default documents / default company. Returns the new case ID.
 */
export const IMPORT_CASE_MUTATION = gql`
  mutation ImportCase($data: JSON!) {
    importCase(data: $data)
  }
`
